// Reusable form field components with Zoho-style labels

function Label({ htmlFor, children, required }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs uppercase tracking-widest text-[#D8D6D0]/60 mb-2">
      {children}
      {required && <span className="text-[#8C1D30] ml-1">*</span>}
    </label>
  );
}

const baseInput =
  "w-full bg-[#0d0d0d] border border-[#D8D6D0]/15 px-3 py-2.5 text-sm text-[#F5F3EF] placeholder-[#D8D6D0]/30 focus:outline-none focus:border-[#8C1D30] transition-colors";

export function TextField({ id, label, value, onChange, required, placeholder, hint, type = "text", testId }) {
  return (
    <div className="mb-5">
      <Label htmlFor={id} required={required}>{label}</Label>
      <input
        id={id}
        data-testid={testId || `field-${id}`}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        className={baseInput}
      />
      {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1.5">{hint}</p>}
    </div>
  );
}

export function TextareaField({ id, label, value, onChange, required, placeholder, hint, rows = 4, testId }) {
  return (
    <div className="mb-5">
      <Label htmlFor={id} required={required}>{label}</Label>
      <textarea
        id={id}
        data-testid={testId || `field-${id}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`${baseInput} font-mono`}
      />
      {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1.5">{hint}</p>}
    </div>
  );
}

export function SelectField({ id, label, value, onChange, options, required, hint, testId }) {
  return (
    <div className="mb-5">
      <Label htmlFor={id} required={required}>{label}</Label>
      <select
        id={id}
        data-testid={testId || `field-${id}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={baseInput}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1.5">{hint}</p>}
    </div>
  );
}

export function CheckboxField({ id, label, value, onChange, hint, testId }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <input
        id={id}
        data-testid={testId || `field-${id}`}
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#8C1D30]"
      />
      <div className="flex-1">
        <label htmlFor={id} className="text-sm text-[#F5F3EF] cursor-pointer">{label}</label>
        {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1">{hint}</p>}
      </div>
    </div>
  );
}

export function FormFooterActions({ onCancel, onSubmit, onDelete, submitLabel = "Save", submitting }) {
  return (
    <div className="flex items-center justify-between gap-3">
      {onDelete ? (
        <button
          type="button"
          data-testid="form-delete-btn"
          onClick={onDelete}
          className="px-4 py-2.5 text-xs uppercase tracking-widest text-[#8C1D30] hover:text-[#F5F3EF] hover:bg-[#8C1D30] transition-colors border border-[#8C1D30]/40"
        >
          Delete
        </button>
      ) : (
        <span />
      )}
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="form-cancel-btn"
          onClick={onCancel}
          className="px-5 py-2.5 text-xs uppercase tracking-widest text-[#D8D6D0]/70 hover:text-[#F5F3EF] border border-[#D8D6D0]/20 hover:border-[#D8D6D0]/50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="form-submit-btn"
          onClick={onSubmit}
          disabled={submitting}
          className="px-6 py-2.5 text-xs uppercase tracking-widest bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] disabled:opacity-50 transition-colors"
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
